'use client'

import { useCallback } from 'react'

declare global {
    interface Window {
        snowplow?: (...args: unknown[]) => void
    }
}

function trackEvent(category: string, action: string, label?: string, property?: string) {
    if (typeof window !== 'undefined' && window.snowplow) {
        window.snowplow('trackStructEvent', {
            category,
            action,
            label,
            property,
        })
    }
}

const CATEGORY = 'search_widget'

export interface SearchSubmitInputs {
    departDate: string
    destination: string
    destinationPlaceId: string
    origin: string
    originPlaceId: string
    returnDate: string
}

export interface SearchSubmitErrors {
    from?: boolean
    to?: boolean
    departDate?: boolean
}

/**
 * Snowplow event tracking for the c2c search widget. Event shape mirrors the
 * `search_widget` contract used by ui-react so the two surfaces aggregate
 * together in Snowplow / Snowflake. Events are silently dropped if the tracker
 * is not loaded.
 */
export function useSearchTracking() {
    const trackFocus = useCallback((field: 'from' | 'to') => {
        trackEvent(CATEGORY, 'focus', field)
    }, [])

    const trackPredictionSelected = useCallback(
        (field: 'from' | 'to', description: string, placeId: string) => {
            trackEvent(CATEGORY, 'inputted', field, JSON.stringify({ description, placeId }))
        },
        [],
    )

    const trackDateSelected = useCallback((date: string) => {
        trackEvent(CATEGORY, 'focus', 'date_selector', JSON.stringify({ date }))
    }, [])

    const trackSubmit = useCallback((inputs: SearchSubmitInputs, errors: SearchSubmitErrors) => {
        const isInvalid = Object.values(errors).some(Boolean)
        trackEvent(
            CATEGORY,
            'submitted',
            isInvalid ? 'invalid' : 'valid',
            JSON.stringify({
                departDate: inputs.departDate,
                destination: {
                    address: inputs.destination,
                    placeId: inputs.destinationPlaceId,
                },
                errors,
                // c2c page has no ClickTripz UI control. Held at `false` so the
                // JSON shape stays byte-equivalent with ui-react's contract.
                find_cheap_hotels: false,
                origin: {
                    address: inputs.origin,
                    placeId: inputs.originPlaceId,
                },
                returnDate: inputs.returnDate,
            }),
        )
    }, [])

    return {
        trackFocus,
        trackPredictionSelected,
        trackDateSelected,
        trackSubmit,
    }
}
